import { useMsal } from "@azure/msal-react";
import { Modal, Table } from "antd";
import { useState, useEffect } from "react";
import { BULK_COST_UPDATE_VALIDATION_COLUMN } from "src/components/bulkCostUpdate/bcuColumns";
import GenericGrid from "src/components/common/Grid/GenericGrid";
import { showErrorNotification } from "src/util/notifications";
import styled from "styled-components";
import { fetchInventoryFinalUpdateValidations } from "./saga";

interface Props {
  param: any;
  modalTitle: any;
}

function InventoryFinalUpdateLinks({ param, modalTitle }: Props) {
  const [open, setOpen] = useState(false);
  const [bcuData, setBcuData] = useState<any[]>([]); // initialize as empty array
  const [loading, setLoading] = useState(false); // loading state
  const [selectedId, setSelectedId] = useState<any>(null); // selected id for fetching
  const { accounts }: any = useMsal();

  const handleLink = (id?: any) => {
    // open modal immediately so we can show a spinner inside it
    setSelectedId(id ?? param);
    setOpen(true);
  };

  // fetch whenever modal opens (and selectedId is set)
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open || !selectedId) return;
      setLoading(true);
      try {
        const res = await fetchInventoryFinalUpdateValidations(selectedId);
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (!mounted) return;
        setBcuData(data);
        if (!data.length) {
          showErrorNotification(`No data found for InventoryFinalUpdateSummaryId (${selectedId})`);
        }
      } catch (error: any) {
        const msg =
          error?.response?.data?.error?.messages?.[0] ||
          error?.message ||
          "An error occurred while fetching validations";
        showErrorNotification(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [open, selectedId]);

  const modelWidth = 1000;
  let content;
  let tblContent;
  content = <StyledLocation>
    <span onClick={() => !loading && handleLink(param)}>{ loading ? "Loading..." : "View Validations" }</span>
  </StyledLocation>;

  tblContent  = <GenericGrid
    key={bcuData.length + '-' + (bcuData[0]?.id ?? '')} // force re-render when data changes
    columns={BULK_COST_UPDATE_VALIDATION_COLUMN}
    data={bcuData}
    enableRowSelection={false}
    rowSelection={[]}
    columnPinning={{}}
    filterDisplayMode="popover"
    highlightFilterMatch={true}
    paginationDisplayMode="pages"
    enablePagination={false}
    enableStickyHeader={true}
    enableStickyFooter={true}
    enableColumnPinning={false}
    isLoading={loading} // wire loading flag
    renderToolbarInternalActions={({  }) => (<></>)} 
    muiTableBodyCellProps={{
      sx: {
        fontSize: '11px',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        padding: '2px 10px'
      },
    }}
    muiTableHeadCellProps={{
      sx: {
        fontSize: '10px',
        fontWeight: '800',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        textAlign: 'center',
        padding: '2px 10px'
      }
    }}
    muiTableBodyRowProps={{
      sx: {
        "& td": {
          padding: "2px 10px",
        },
        textAlign: 'left',
      },
    }}
  ></GenericGrid>;
  return (
    <>
      {content}
      <Modal
        title={modalTitle}
        okText="Ok"
        centered
        width={modelWidth}
        open={open}
        onCancel={() => setOpen(false)}
        footer={false}
        destroyOnClose={true}  // ensures child unmount on close
      >
        {loading ? <div>Loading...</div> : tblContent}
      </Modal>
    </>
  );
}

const StyledLocation = styled.div`
  color: #40668c;
  text-decoration: underline;
  cursor: pointer;
`;

const StyledItem = styled.div`
  align: "center";
`;

export default InventoryFinalUpdateLinks;
